import type { SubscriptionV1 } from 'entities/subscription';
import {
  CHAPTER_UNLOCK_DAYS_SINCE_DELIVERY,
  getElapsedDaysSince,
} from 'lib/member-experience';
import { ProductCodes } from './utils/get-product-specific-code';

const activeStates = ['active', 'future', 'paused'];
const reactivatableStates = ['canceled', 'expired'];

export const DEFAULT_MEMBER_EXPERIENCE_CHAPTER = 1;

// An order that never reports a delivery (e.g. a cancelled test order) counts
// as delivered this many days after purchase, so the journey still advances.
export const ASSUMED_DELIVERY_DAYS_AFTER_PURCHASE = 10;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const subscriptionGroup = (subscription: SubscriptionV1): string | undefined =>
  subscription.productSku?.split('-')[0]?.toLowerCase();

// The member's earliest-activated subscription to the product — their original
// purchase, whose dates anchor the journey's time-based progress.
export const earliestSubscription = (
  subscriptions: SubscriptionV1[] | undefined,
  productGroup: ProductCodes,
): SubscriptionV1 | undefined =>
  (subscriptions ?? [])
    .filter((subscription) => subscriptionGroup(subscription) === productGroup)
    .sort(
      (a, b) =>
        new Date(a.activatedAt).getTime() - new Date(b.activatedAt).getTime(),
    )[0];

export const getPurchaseDate = (
  subscriptions: SubscriptionV1[] | undefined,
  productGroup: ProductCodes,
): string | undefined =>
  earliestSubscription(subscriptions, productGroup)?.activatedAt;

// The recorded initial delivery of the original purchase, or, when it never
// reported one, an assumed delivery date once that date has passed. undefined
// until then, so the progress bar and the chapter below always agree.
export const getInitialDeliveryDate = (
  subscriptions: SubscriptionV1[] | undefined,
  productGroup: ProductCodes,
): string | undefined => {
  const subscription = earliestSubscription(subscriptions, productGroup);
  if (!subscription) return undefined;
  if (subscription.initialDeliveredAt) return subscription.initialDeliveredAt;
  const assumedDeliveredAt =
    new Date(subscription.activatedAt).getTime() +
    ASSUMED_DELIVERY_DAYS_AFTER_PURCHASE * MS_PER_DAY;
  // NaN here means a malformed activatedAt, which must not throw in render.
  if (!Number.isFinite(assumedDeliveredAt) || assumedDeliveredAt > Date.now()) {
    return undefined;
  }
  return new Date(assumedDeliveredAt).toISOString();
};

// The member's furthest unlocked chapter: one per threshold passed since their
// original purchase was (or is assumed to have been) delivered, never below 1.
export const getMemberExperienceChapter = (
  subscriptions: SubscriptionV1[] | undefined,
  productGroup: ProductCodes,
): number => {
  const daysSinceDelivery = getElapsedDaysSince(
    getInitialDeliveryDate(subscriptions, productGroup),
  );
  const unlockedChapters = CHAPTER_UNLOCK_DAYS_SINCE_DELIVERY.filter(
    (unlockDay) => daysSinceDelivery >= unlockDay,
  ).length;
  return Math.max(unlockedChapters, DEFAULT_MEMBER_EXPERIENCE_CHAPTER);
};

const isSubscriptionActive = (subscription) =>
  activeStates.indexOf(subscription.state) >= 0;

export const hasActiveSubscriptionFor = (
  subscriptions: SubscriptionV1[] | undefined,
  productGroup: ProductCodes,
): boolean =>
  (subscriptions ?? []).some(
    (subscription) =>
      subscriptionGroup(subscription) === productGroup &&
      isSubscriptionActive(subscription),
  );

const isSubscriptionPaused = (subscription) =>
  subscription.subscription_state === 'PA';

const canSubscriptionBeReactivated = (subscription) =>
  reactivatableStates.indexOf(subscription.state) >= 0;

export {
  activeStates,
  reactivatableStates,
  isSubscriptionActive,
  isSubscriptionPaused,
  canSubscriptionBeReactivated,
};
